import React, { useState, useEffect } from "react";
import Select, { ActionMeta, StylesConfig, MultiValue, SingleValue } from 'react-select';
import { query_string, query_number, query_boolean, query_color } from "./queries";
import { iter, iter_map } from "./iter";
import MyButton from "./MyButton";
import MyCheckbox from "./MyCheckbox";
import MyInput from "./MyInput";
import { shadeColor } from "./shade";
import { ApiError, CRMContact, api_get_account_contacts, api_get_calendar, api_get_contact, api_get_slots, api_register_event } from "../slots/api";
import { Calendar, CalendarSlot, CalendarEventRequest } from "../slots/api";
import { Hour, compute_hours_from_slots } from "./helper";

const weekDaysFR = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const monthNamesFR = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"]
const itemsPerPage = 12;

// ---

const humanHour = (t: number): string => {
    const h = Math.floor(t);
    const m = Math.round((t - h) * 60);
    return `${h} h ${m.toString().padStart(2, '0')}`;
}

// ---

interface UtcDate {
    year: number,
    month: number,
    day: number,
    hour: number,
    minute?: number,
}

// ---

const isValidEmail = (email: string): boolean => {
    return email.match(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/) != null
}

const isValidPhone = (phone: string): boolean => {
    return phone.match(/^(?:(?:(?:\+|00)33\D?(?:\D?\(0\)\D?)?)|0){1}[1-9]{1}(?:\D?\d{2}){4}$/) != null
}

const fixPhone = (phone: string): string => {
    return phone.replace(/[,\. \-:]+/g, '').replace(/^0([^0])/, '+33$1')
}

// ---

interface Option {
    value: number,
    label: string,
    isDisabled: boolean,
    current: boolean,
}

interface NewOption {
    value: number,
    label1: string,
    label2: string,
    isDisabled: boolean,
    current: boolean,
}

const getLabel = (option: Option) => option.label;
const getValue = (option: Option) => option.value.toFixed(0);

const uuid = window.location.pathname.replace(/\//g, "");
const token = query_string("token");

const hide_resolution = query_boolean("hide-resolution");

const origin = query_string("origin", "https://form.sorenir.app");
const rounded = query_boolean("rounded");
const debug = query_string("debug");
const deferredGuid = query_string("dguid");
const deferredTime = query_string("dtime");

const showForm = query_boolean("show-form", false);

const alternateGuid = query_string("alt-guid");
const noConfirmEmail = query_string("no-confirm-email") == "true";

const button_color1 = query_color("color", "34898A"); // 198754
const button_text_color1 = query_color("tcolor", "ffffff");

const button_color2 = query_color("color2", button_color1);
const button_text_color2 = query_color("tcolor2", "ffffff");

const one_line_submit = query_boolean("one-line-submit");
const highlight_today = query_boolean("highlight-today");

const def_simple_view = query_number("simple-view", -1, 2, 20);
const def_max_days = query_number("max-days", -1, def_simple_view, 20);

const def_company = query_string("company", "");

const accountToCall = query_string("account-to-call");

const showTodaysSlots = query_boolean("show-todays-slots", false);

const iframeFrom = query_string("from", "unknown");

const contact = query_string("contact");
console.log(`root: contact=${contact}`);

const account = query_string("account");
console.log(`root: account=${account}`);

const parameters = {
    bgcolor: query_color("bgcolor", "ffffff"),
    textColor: query_color("textcolor", "000000"),
    errColor: query_color("errorcolor", "ff0000"),

    // ++++++++++++++++

    // button1: background-color, border-color, text-color
    color: button_color1,
    bcolor: query_color("bcolor", button_color1),
    tcolor: button_text_color1,

    // button1: disabled-color: background and text
    dcolor: query_color("dcolor", `9B9B9B`),
    dbcolor: query_color("dbcolor", shadeColor(button_color1, 1.2)),
    dtcolor: query_color("dtcolor", button_text_color1),

    // button1: selected-color: background and text
    scolor: query_color("scolor", shadeColor(button_color1, 1.8)),
    sbcolor: query_color("sbcolor", "C09D38"), // shadeColor(button_color1, 1.8)),
    stcolor: query_color("stcolor", button_text_color1),

    // button1: hover-color: background and text
    hcolor: query_color("hcolor", shadeColor(button_color1, 0.8)),
    hbcolor: query_color("hbcolor", shadeColor(button_color1, 0.8)),
    htcolor: query_color("htcolor", button_text_color1),

    // ++++++++++++++++

    // button2: background-color, border-color, text-color
    color2: button_color2,
    bcolor2: query_color("bcolor2", button_color2),
    tcolor2: button_text_color2,

    // button2: disabled-color: background and text
    dcolor2: query_color("dcolor2", `555555`),
    dbcolor2: query_color("dbcolor2", shadeColor(button_color2, 1.2)),
    dtcolor2: query_color("dtcolor2", button_text_color2),

    // button2: selected-color: background and text
    scolor2: query_color("scolor2", shadeColor(button_color2, 1.8)),
    sbcolor2: query_color("sbcolor2", shadeColor(button_color2, 1.8)),
    stcolor2: query_color("stcolor2", button_text_color2),

    // button2: hover-color: background and text
    hcolor2: query_color("hcolor2", shadeColor(button_color2, 0.8)),
    hbcolor2: query_color("hbcolor2", shadeColor(button_color2, 0.8)),
    htcolor2: query_color("htcolor2", button_text_color2),

    // ++++++++++++++++

    // input: background-color, border-color, text-color
    input: query_color("input", "ffffff"),
    binput: query_color("binput", "999999"),
    tinput: query_color("tinput", "000000"),

    // input: disabled-color: background and text
    dinput: query_color("dinput", "cccccc"),
    dbinput: query_color("dbinput", "999999"),
    dtinput: query_color("dtinput", "333333"),

    // input: selected-color: background and text
    sinput: query_color("sinput", "ffffff"),
    sbinput: query_color("sbinput", "198754"),
    stinput: query_color("stinput", "000000"),

    // input: hover-color: background and text
    hinput: query_color("hinput", "ffffff"),
    hbinput: query_color("hbinput", "999999"),
    htinput: query_color("htinput", "000000"),

    // input: error-color: background and text
    einput: query_color("einput", "ffffff"),
    ebinput: query_color("ebinput", "FF0000"),
    etinput: query_color("etinput", "FF0000"),

    // ++++++++++++++++

    fontSize: `${query_number("fontsize", 18, 10, 34)}px`,
    altFontSize: `${query_number("fontsize", 18, 10, 34) * 0.9}px`,
    fontFamily: query_string("fontfamily", "jost", ["inherit", "montserrat", "jost", "futura"]),
    bpadding: `${query_number("bpadding", 10, 0, 34)}px`,
};

if (debug.indexOf("params") >= 0) {
    console.log(`parameters %o`, parameters);
}

if (debug.indexOf("sizes") >= 0) {
    console.log(`iframe size: innerHeight=${window.innerHeight}, outerHeight=${window.outerHeight}`,)
}

// ----------------------------------------------------------- styles

const defaultStyle = {
    fontSize: parameters.fontSize,
    fontFamily: parameters.fontFamily,
};

// ----------------------------------------------------------- calendar buttons [color1]

const calendarBtnDefaultStyle = {
    borderRadius: rounded ? "50%" : "5px",
    ...defaultStyle,
}

const calendarBtnDisabledStyle = {
    backgroundColor: `#${parameters.dcolor}`,
    borderColor: `#${parameters.dcolor}`,
    color: `#${parameters.dtcolor}`,
    cursor: `no-drop`,
    ...calendarBtnDefaultStyle,
};

const calendarBtnEnabledStyle = {
    backgroundColor: `#${parameters.color}`,
    borderColor: `#${parameters.color}`,
    color: `#${parameters.tcolor}`,
    ...calendarBtnDefaultStyle,
};

const calendarBtnHoverStyle = {
    backgroundColor: `#${parameters.hcolor}`,
    borderColor: `#${parameters.hcolor}`,
    color: `#${parameters.htcolor}`,
    ...calendarBtnDefaultStyle,
};

const calendarBtnMarkedStyle = highlight_today ? {
    backgroundColor: `#${parameters.color}`,
    borderColor: `#${parameters.scolor}`,
    color: `#${parameters.tcolor}`,
    ...calendarBtnDefaultStyle,
} : calendarBtnEnabledStyle;

const calendarBtnSelectedStyle = {
    backgroundColor: `#${parameters.sbcolor}`,
    borderColor: `#${parameters.scolor}`,
    color: `#${parameters.stcolor}`,
    ...calendarBtnDefaultStyle,
}

// ----------------------------------------------------------- hour buttons [color1]

const hourBtnDefaultStyle = {
    borderRadius: rounded ? "8px" : "2px",
    ...defaultStyle,
}

const hourBtnDisabledStyle = {
    backgroundColor: `#${parameters.dcolor}`,
    borderColor: `#${parameters.dcolor}`,
    color: `#${parameters.dtcolor}`,
    cursor: `no-drop`,
    ...hourBtnDefaultStyle,
};

const hourBtnEnabledStyle = {
    backgroundColor: `#${parameters.color}`,
    borderColor: `#${parameters.color}`,
    color: `#${parameters.tcolor}`,
    ...hourBtnDefaultStyle,
};

const hourBtnHoverStyle = {
    backgroundColor: `#${parameters.hcolor}`,
    borderColor: `#${parameters.hcolor}`,
    color: `#${parameters.htcolor}`,
    ...hourBtnDefaultStyle,
};

const hourBtnSelectedStyle = {
    backgroundColor: `#${parameters.sbcolor}`,
    borderColor: `#${parameters.scolor}`,
    color: `#${parameters.stcolor}`,
    ...hourBtnDefaultStyle,
}

// ----------------------------------------------------------- form buttons [color2]

const formBtnDefaultStyle = {
    borderRadius: rounded ? "8px" : "2px",
    paddingTop: `${parameters.bpadding}px`,
    paddingBottom: `${parameters.bpadding}px`,
    ...defaultStyle,
}

const formBtnDisabledStyle = {
    backgroundColor: `#${parameters.dcolor2}`,
    borderColor: `#${parameters.dcolor2}`,
    color: `#${parameters.dtcolor2}`,
    cursor: `no-drop`,
    ...formBtnDefaultStyle,
};

const formBtnEnabledStyle = {
    backgroundColor: `#${parameters.color2}`,
    borderColor: `#${parameters.bcolor2}`,
    color: `#${parameters.tcolor2}`,
    ...formBtnDefaultStyle,
};

const formBtnHoverStyle = {
    backgroundColor: `#${parameters.hcolor2}`,
    borderColor: `#${parameters.hcolor2}`,
    color: `#${parameters.htcolor2}`,
    ...formBtnDefaultStyle,
};

// ----------------------------------------------------------- civility buttons [color1]

const civilityBtnDefaultStyle = {
    borderRadius: rounded ? "8px" : "2px",
    paddingTop: `${parameters.bpadding}px`,
    paddingBottom: `${parameters.bpadding}px`,
    ...defaultStyle,
}

const civilityBtnEnabledStyle = {
    backgroundColor: `#${parameters.bgcolor}`,
    borderColor: `#${parameters.color}`,
    color: `#${parameters.color}`,
    ...civilityBtnDefaultStyle,
};

const civilityBtnHoverStyle = {
    backgroundColor: `#${parameters.hcolor}`,
    borderColor: `#${parameters.hbcolor}`,
    color: `#${parameters.htcolor}`,
    ...civilityBtnDefaultStyle,
};

const civilityBtnSelectedStyle = {
    backgroundColor: `#${parameters.color}`,
    borderColor: `#${parameters.color}`,
    color: `#${parameters.bgcolor}`,
    cursor: `no-drop`,
    ...civilityBtnDefaultStyle,
};

// ----------------------------------------------------------- input [input]

const inputDefaultStyle = {
    borderRadius: rounded ? "8px" : "2px",
    ...defaultStyle,
}

const inputEnabledStyle = {
    backgroundColor: `#${parameters.input}`,
    borderColor: `#${parameters.binput}`,
    color: `#${parameters.tinput}`,
    ...inputDefaultStyle,
};

const checkBoxEnabledStyle = {
    backgroundColor: `#${parameters.color}`,
    // backgroundColor: `transparent`,
    borderColor: `#${parameters.dinput}`,
    borderRadius: rounded ? "16px" : "2px",
};

const checkBoxFocusStyle = {
    backgroundColor: `#${parameters.dinput}`,
    // backgroundColor: "transparent",
    borderColor: `#${parameters.dinput}`,
    borderRadius: rounded ? "16px" : "2px",
};

const inputDisabledStyle = {
    backgroundColor: `#${parameters.dinput}`,
    borderColor: `#${parameters.dbinput}`,
    color: `#${parameters.dtinput}`,
    cursor: `no-drop`,
    ...inputDefaultStyle,
}

const inputHoverStyle = {
    backgroundColor: `#${parameters.hinput}`,
    borderColor: `#${parameters.hbinput}`,
    color: `#${parameters.htinput}`,
    ...inputDefaultStyle,
};

const inputFocusStyle = {
    backgroundColor: `#${parameters.sinput}`,
    borderColor: `#${parameters.sbinput}`,
    color: `#${parameters.stinput}`,
    ...inputDefaultStyle,
};

const inputErrorStyle = {
    backgroundColor: `#${parameters.einput}`,
    borderColor: `#${parameters.ebinput}`,
    color: `#${parameters.etinput}`,
    ...inputDefaultStyle,
}

// -----------------------------------------------------------

const textStyle = {
    color: `#${parameters.textColor}`,
    ...defaultStyle,
}

const smallTextStyle = {
    color: `#${parameters.textColor}`,
    ...defaultStyle,
}

smallTextStyle.fontSize = parameters.altFontSize;

const textErrorStyle = {
    color: `#${parameters.errColor}`,
    ...defaultStyle,
}

// -----------------------------------------------------------

const defCivility = query_string("civility", "Mme");
const defFirstName = query_string("firstname");
const defLastName = query_string("lastname");
const defEmail = query_string("email");
const defPhone = query_string("phone");

const defDeceasedCivility = query_string("civility", "Mme");
const defDeceasedFirstName = query_string("firstname");
const defDeceasedLastName = query_string("lastname");
const defDeceasedEmail = query_string("email");
const defDeceasedPhone = query_string("phone");

const defHideForm = query_boolean("hide-form", false);
const defDoSpace = query_boolean("space", false);
const defNoScroll = query_boolean("no-scroll", false);

console.log(`message: defHideForm = ${defHideForm ? "true" : "false"}`);
console.log(`message: defDoSpace  = ${defDoSpace ? "true" : "false"}`);
console.log(`message: defNoScroll = ${defNoScroll ? "true" : "false"}`);

const defTitle = query_string("title");

function is_valid_uuid(uuid: string): boolean {
    const regex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    return regex.test(uuid);
}

export const useRefScrollTo = (scrollProps: ScrollIntoViewOptions) => {
    const scrollToTop = React.useRef<HTMLInputElement>(null);
    if (!defNoScroll) {
        React.useEffect(() => {
            console.log(`scrollIntoView`);
            scrollToTop.current && scrollToTop.current.scrollIntoView({ ...scrollProps });
        });
    }
    return scrollToTop;
}

function Form() {
    const [calendar, setCalendar] = React.useState<Calendar | null>(null);
    const [error, setError] = React.useState("");
    const [canRetry, setCanRetry] = React.useState(false);

    const [simple_view, setSimpleView] = React.useState(def_simple_view);

    const [lastDayOfMonth, setLastDayOfMonth] = React.useState(0);
    const [firstDayOfMonth, setFirstDayOfMonth] = React.useState(0);

    const [selectedYear, setSelectedYear] = React.useState<Option | null>(null);
    const [selectedMonth, setSelectedMonth] = React.useState<Option | null>(null);
    const [selectedDay, setSelectedDay] = React.useState(-1);
    const [selectedHour, setSelectedHour] = React.useState(-1);

    const [selectedRendezvous, SetSelectedRendezvous] = React.useState<NewOption | null>(null);

    const [monthSlots, setMonthSlots] = React.useState([] as CalendarSlot[]);
    const [daySlots, setDaySlots] = React.useState<CalendarSlot[] | null>(null);
    const [hours, setHours] = React.useState<Hour[]>([]);

    const [loadingSlots, SetLoadingSlots] = React.useState(false);

    const [title, setTitle] = React.useState("Votre rendez-vous");
    const [resolution, setResolution] = React.useState(30); // in minutes

    const [thisMonth, setThisMonth] = React.useState(-1);
    const [thisYear, setThisYear] = React.useState(-1);
    const [thisDay, setThisDay] = React.useState(-1);

    const [page, setPage] = React.useState(0);

    const [yearOptions, setYearOptions] = React.useState([] as Option[]);
    const [rendezvousOptions, setRendezvousOptions] = React.useState([] as NewOption[]);
    const [monthOptions, setMonthOptions] = React.useState([] as Option[]);

    const [reservedSlot, setReservedSlot] = React.useState(showForm);
    const [registered, setRegistered] = React.useState<string | null>(null);
    const [alreadyTaken, setAlreadyTaken] = useState(false);

    const [inviteeCivility, setInviteeCivility] = useState(defCivility);
    const [inviteeFirstName, setInviteeFirstName] = useState(defFirstName);
    const [inviteeLastName, setInviteeLastName] = useState(defLastName);
    const [inviteeEmail, setInviteeEmail] = useState(defEmail);
    const [inviteePhone, setInviteePhone] = useState(defPhone);

    const [deceasedCivility, setDeceasedCivility] = useState(defDeceasedCivility);
    const [deceasedFirstName, setDeceasedFirstName] = useState(defDeceasedFirstName);
    const [deceasedLastName, setDeceasedLastName] = useState(defDeceasedLastName);
    const [deceasedEmail, setDeceasedEmail] = useState(defDeceasedEmail);
    const [deceasedPhone, setDeceasedPhone] = useState(defDeceasedPhone);

    const [inputs, setInputs] = useState(new Set<string>());

    const [msgSource, setMsgSource] = useState<MessageEventSource | null>(null);
    const [msgOrigin, setMsgOrigin] = useState(origin);
    const [rootHeight, setRootHeight] = useState(0);

    const [canRegister, setCanRegister] = useState(false);

    const [contactUuidParameter, _setcontactUuid] = React.useState(contact);
    const [accountUuidParameter, _setaccountUuid] = React.useState(account);
    const [paramError, setParamError] = React.useState("");
    const [loadingCrmInfo, setLoadingCrmInfo] = React.useState(true);
    const [crmContacts, setCrmContacts] = React.useState([] as CRMContact[]);

    const [accountGuid, setAccountGuid] = React.useState<string | undefined>();
    const [contactGuid, setContactGuid] = React.useState<string | undefined>();

    const [showReserve, setShowReserve] = React.useState(false);

    const [registrationPending, setRegistrationPending] = React.useState(false);

    const [calTicket, setCalTicket] = React.useState(0);

    // ---

    React.useEffect(() => {
        console.log(`root: initializing uuid parameters`);

        console.log(`root: contactUuid=${contactUuidParameter}`);
        if (contactUuidParameter !== "") {
            console.log(`contact-uuid: ${contactUuidParameter}`);

            if (!is_valid_uuid(contactUuidParameter)) {
                setParamError("Contact invalide");
                return;
            }

            api_get_contact(contactUuidParameter, token)
                .then(c => {
                    console.log('found contact: %o', c);
                    setContactGuid(c.uuid);
                    setInviteeCivility(old => old == "" ? c.civility : old);
                    setInviteeFirstName(old => old == "" ? c.first_name : old);
                    setInviteeLastName(old => old == "" ? c.last_name : old);
                    setInviteeEmail(old => old == "" ? c.email : old);
                    setInviteePhone(old => old == "" ? c.phone : old);
                    setCrmContacts([c]);
                })
                .catch(setError)
                .finally(() => {
                    setLoadingCrmInfo(false);
                })
                ;
        }

        console.log(`root: accountUuid=${accountUuidParameter}`);
        if (accountUuidParameter !== "") {
            console.log(`account-uuid: ${accountUuidParameter}`);

            if (!is_valid_uuid(accountUuidParameter) && (accountUuidParameter !== "me")) {
                setParamError("Compte défunt invalide");
                return;
            }

            if ((contactUuidParameter === "") || (accountUuidParameter !== "me")) {
                api_get_account_contacts(accountUuidParameter, token)
                    .then(c => {
                        console.log('found accounts: %o', c);
                        if (c.length == 0) {
                            setError("Ce compte n'a pas de contact associé");
                            return;
                        }
                        setAccountGuid(accountUuidParameter);

                        setContactGuid(c[0].uuid);
                        setInviteeCivility(old => old == "" ? c[0].civility : old);
                        setInviteeFirstName(old => old == "" ? c[0].first_name : old);
                        setInviteeLastName(old => old == "" ? c[0].last_name : old);
                        setInviteeEmail(old => old == "" ? c[0].email : old);
                        setInviteePhone(old => old == "" ? c[0].phone : old);
                        setCrmContacts(c);
                    })
                    .catch(setError)
                    .finally(() => {
                        setLoadingCrmInfo(false);
                    })
                    ;
            } else {
                console.log(`setAccountGuid: ${accountUuidParameter}`);
                setAccountGuid(accountUuidParameter);
            }
        }

        setLoadingCrmInfo(false);
    }, [contactUuidParameter, accountUuidParameter]);

    // ---

    useEffect(() => {
        document.body.style.backgroundColor = `#${parameters.bgcolor}`;
    }, []);

    // Post message to parent when registered 
    React.useEffect(() => {
        if (debug.indexOf("message") >= 0) console.log(`message: registered: ${registered}, msgSource: %o`, msgSource);
        if (registered !== null && msgSource !== null && msgOrigin !== null) {
            let sd: null | UtcDate = null;
            if (selectedRendezvous) {
                const date = new Date(selectedRendezvous.value);
                const year = date.getFullYear();
                const month = date.getMonth();
                const day = date.getDate();
                const hour = date.getHours();
                const minute = date.getMinutes();
                sd = {
                    year,
                    month,
                    day,
                    hour: hour + (minute / 60.0),
                }
            }

            const msg: any = {
                event: "calendar.event_scheduled",
                ticket: calTicket,
                guid: registered,
                payload: {
                    dates: [
                        {
                            year: sd ? sd.year : selectedYear?.value,
                            month: sd ? sd.month : selectedMonth?.value,
                            day: sd ? sd.day : selectedDay,
                            hour: sd ? sd.hour : selectedHour,
                        },
                    ],
                    persons: [
                        {
                            civility: inviteeCivility,
                            first_name: inviteeFirstName,
                            last_name: inviteeLastName,
                            email: inviteeEmail,
                            phone: fixPhone(inviteePhone),
                        },
                    ]
                }
            };
            if (false) {
                msg.deceased = {
                    civility: deceasedCivility,
                    first_name: deceasedFirstName,
                    last_name: deceasedLastName,
                    email: deceasedEmail,
                    phone: fixPhone(deceasedPhone),
                }
            }
            console.log(`message: %o`, msg);
            msgSource.postMessage(msg, msgOrigin as any);
        }
    }, [registered]);

    // send Resize message to parent
    React.useEffect(() => {
        if (debug.indexOf("resize") >= 0) {
            console.log(msgSource);
            console.log(msgOrigin);
        }
        if (msgSource && msgOrigin) {
            console.log(`message: iframe-height: ${rootHeight}`);
            msgSource.postMessage({
                event: "calendar.resize",
                ticket: calTicket,
                iframeHeight: rootHeight + 10,
            }, msgOrigin as any);
        }
    }, [rootHeight, msgSource, msgOrigin]);

    // handle window resize
    React.useEffect(() => {
        const rootEl = document.querySelector('#root');
        if (rootEl) {
            const on_resize = () => {
                const rect = rootEl.getBoundingClientRect();
                if (rootHeight != rect.height) {
                    setRootHeight(rect.height);
                    // window.dispatchEvent(new Event('resize'));
                }
            }
            window.addEventListener('resize', on_resize);
            on_resize();
            return () => {
                window.removeEventListener('resize', on_resize);
            }
        }
    });

    // get calendar once loaded
    React.useEffect(() => {
        if (debug.indexOf("all") >= 0) console.log(`calendar: loading`);

        api_get_calendar(uuid, token)
            .then((calendar: Calendar) => {
                calendar.now = new Date(calendar.now);
                setCalendar(calendar);


                let allKeys: { [key: string]: string } = {};
                for (let key in calendar.calendar_metas) {
                    if (key.startsWith("input:")) {
                        allKeys[key] = calendar.calendar_metas[key];
                    }
                }
                for (let key in calendar.instance_metas) {
                    if (key.startsWith("input:")) {
                        let v = calendar.instance_metas[key];
                        if (v == "") {
                            delete allKeys[key];
                        } else {
                            allKeys[key] = v;
                        }
                    }
                }

                let tmpInputs = new Set<string>();
                for (let key in allKeys) {
                    tmpInputs.add(calendar.instance_metas[key])
                }

                setInputs(tmpInputs);
            })
            .catch(setError)
            ;
    }, [])

    // once calendar uploaded, update data
    React.useEffect(() => {
        if (calendar == null) {
            return;
        }

        if (debug.indexOf("all") >= 0) console.log(`calendar: got calendar, initializing ui`);

        // set resolution
        if (calendar.calendar_metas["resolution"]) {
            setResolution(parseInt(calendar.calendar_metas["resolution"], 10) || 6);
        }
        if (calendar.instance_metas["resolution"]) {
            setResolution(parseInt(calendar.instance_metas["resolution"], 10) || 6);
        }

        // set title
        if (defTitle == "") {
            if (calendar.calendar_metas["title"]) {
                setTitle(calendar.calendar_metas["title"]);
            }
            if (calendar.instance_metas["title"]) {
                setTitle(calendar.instance_metas["title"]);
            }
        } else {
            setTitle(defTitle);
        }

        if (thisDay == -1) {
            setThisDay(calendar.now.getDate());
        }

        const tmpYear = calendar.now.getFullYear();

        const list = [] as Option[];
        for (let year in calendar.dates) {
            list.push({
                label: year,
                value: parseInt(year, 10),
                isDisabled: false,
                current: (parseInt(year, 10) == tmpYear),
            })
        }

        setYearOptions(list);
        if (selectedYear == null) {
            setThisYear(calendar.now.getFullYear());
            setSelectedYear(list[0]);
        }

        const calendar_has_form = calendar.calendar_metas["has-form"];
        const calendar_has_default = calendar.calendar_metas["has-default"];
        const instance_has_form = calendar.instance_metas["has-form"];
        const instance_has_default = calendar.instance_metas["has-default"];

        let has_form = false;
        if (calendar_has_form === "true") {
            if (instance_has_form !== "false") {
                has_form = true;
            }
        } else {
            if (instance_has_form === "true") {
                has_form = true;
            }
        }
        console.log(`has_form: ${has_form} : ${calendar_has_form}/${instance_has_form}`);

        let has_default = false;
        if (calendar_has_default === "true") {
            if (instance_has_default !== "false") {
                has_default = true;
            }
        } else {
            if (instance_has_default === "true") {
                has_default = true;
            }
        }
        console.log(`has_default: ${has_default} : ${calendar_has_default}/${instance_has_default}`);

        // we need default parameters
        if (!has_form) {
            if (inviteeEmail == "") {
                if (has_default) {
                    setInviteeCivility(old => old == "" ? "M" : old);
                    setInviteeFirstName(old => old == "" ? "Gaston" : old);
                    setInviteeLastName(old => old == "" ? "Lagaffe" : old);
                    setInviteeEmail(old => old == "" ? "gaston@laplage.fr" : old);
                    setInviteePhone(old => old == "" ? "+33600000000" : old);
                } else {
                    console.error(`has-default=true, but inviteeEmail is empty`);
                    setError("Il manque les paramêtres par défaut");
                }
            }
        } else if (def_simple_view <= 0) {
            // Do not show "fill form data"
            if (!defHideForm) setShowReserve(true);
        }
    }, [calendar])

    // once a year is selected
    React.useEffect(() => {
        if (calendar == null)
            return;

        if (selectedYear == null)
            return;

        const tmpMonth = calendar.now.getMonth() + 1;

        if (debug.indexOf("all") >= 0) console.log(`calendar: year updated, initializing month list, thisYear=${thisYear} thisMonth=${tmpMonth}, selectedYear=%o`, selectedYear);

        let tmp = -1;
        const list = [] as Option[];
        for (let month = 1; month <= 12; month += 1) {
            console.log(`selectedYear.value: ${selectedYear.value}`);
            const disabled = calendar.dates[selectedYear.value][month] == undefined;
            const current = (selectedYear.value == thisYear) && (month == tmpMonth);

            if (current) {
                if (debug.indexOf("all") >= 0) console.log(`calendar: current: thisYear=${thisYear} thisMonth=${tmpMonth}`);
            }

            if (!disabled && tmp == -1) {
                tmp = list.length;
            }

            list.push({
                label: monthNamesFR[month - 1],
                value: month,
                isDisabled: disabled,
                current: current,
            })
        }

        setMonthOptions(list);
        if (selectedMonth == null && tmp != -1 /* && thisMonth == -1 */) {
            setThisMonth(tmpMonth);
            setSelectedMonth(list[tmp]);
        }
    }, [selectedYear])

    // if both year and month have been select
    React.useEffect(() => {
        if (selectedYear == null || selectedMonth == null)
            return;

        if (debug.indexOf("all") >= 0) {
            console.log(`calendar: year or month updated, initializing variables`);
        }

        setSelectedDay(-1);
        setSelectedHour(-1);
        const tmpNext = new Date(selectedYear.value, selectedMonth.value, 0);
        setLastDayOfMonth(tmpNext.getDate());
        const tmpCurr = new Date(selectedYear.value, selectedMonth.value - 1, 1);
        setFirstDayOfMonth(tmpCurr.getDay())

        if (debug.indexOf("all") >= 0) {
            console.log(`calendar: tmpCurr=${tmpCurr}, tmpNext=${tmpNext}, selectedYear=${selectedYear.value}, selectedMonth=${selectedMonth.value}`);
        }

        SetLoadingSlots(true);
        console.log(`rvslots: loading...`);
        api_get_slots(uuid, token, selectedYear.value, selectedMonth.value, undefined, simple_view > 0 ? 10 : undefined)
            .finally(() => {
                SetLoadingSlots(false);
            })
            .then(slots => {
                setMonthSlots(slots
                    .map(slot => {
                        if (typeof slot.date === "string") {
                            slot.date = new Date(slot.date);
                        }
                        return slot;
                    })
                    .filter(slot => {
                        if (calendar && def_simple_view > 0) {
                            const tendays = calendar.now.getTime() + 1000 * 60 * 60 * 24 * 10;
                            const currdate = slot.date.getTime();
                            console.log(`currdate %o`, new Date(currdate));
                            console.log(`tendays %o`, new Date(tendays));
                            if (currdate < tendays) {
                                return true;
                            }
                            return false;
                        }
                        return true;
                    })
                );

                console.log(`rvslots: computing`);
                if (simple_view > 0) {
                    console.log(`slots: %o`, slots);

                    const now = calendar?.now || new Date();

                    let filtered_slots = slots
                        .filter(slot => {
                            if (slot.count <= 0) {
                                return false;
                            }
                            if (slot.duration > 60 * 24) {
                                return false;
                            }
                            const diff = slot.date.getTime() - now.getTime();
                            if (diff < 0 || diff > 10 * 24 * 60 * 60 * 1000) {
                                return false;
                            }
                            return true;
                        })
                        ;

                    // debugger;
                    console.log(`rvslots: filtered slots: %o`, filtered_slots);

                    setMonthSlots(slots
                        .map(slot => {
                            if (typeof slot.date === "string") {
                                slot.date = new Date(slot.date);
                            }
                            return slot;
                        })
                        .filter(slot => {
                            if (calendar && def_simple_view > 0) {
                                const tendays = calendar.now.getTime() + 1000 * 60 * 60 * 24 * 10;
                                const currdate = slot.date.getTime();
                                // console.log(`currdate %o`, new Date(currdate));
                                // console.log(`tendays %o`, new Date(tendays));
                                if (currdate < tendays) {
                                    return true;
                                }
                                return false;
                            }
                            return true
                        })
                    );

                    let splitted_slots = filtered_slots
                        .map(slot => {
                            const splitted: CalendarSlot[] = [];

                            // console.log(`rvslots: adding sect duration ${slot.duration}`);
                            while (slot.duration >= resolution) {
                                // console.log(`rvslots: adding slot %o`, slot.date);
                                splitted.push({
                                    date: slot.date,
                                    duration: resolution,
                                    count: slot.count,
                                })
                                slot.duration -= resolution;
                                slot.date = new Date(slot.date.getTime() + resolution * 60 * 1000);
                            }

                            return splitted;
                        })
                        .flat()
                        ;

                    console.log(`rvslots: splitted slots: %o`, splitted_slots);

                    const random_picked_slots: CalendarSlot[] = [];
                    let interval = splitted_slots.length / def_simple_view;

                    for (let i = 0; i < def_simple_view; i++) {
                        // Ajout d'un léger décalage aléatoire (par exemple, +/- 25% de l'intervalle)
                        const randomOffset = Math.floor(Math.random() * interval - interval * 0.5);
                        const index = Math.min(Math.max(0, Math.floor(i * interval + randomOffset)), splitted_slots.length - 1);
                        const picked_slot = splitted_slots[index];
                        if (picked_slot) {
                            random_picked_slots.push(picked_slot);
                        }
                    }

                    console.log(`rvslots: randomly picked slots: %o`, random_picked_slots.flat());

                    const rvslots = random_picked_slots
                        .map(slot => {
                            const rv_year = slot.date.getFullYear();
                            const rv_month = slot.date.getMonth(); // pfff
                            const rv_day = slot.date.getDate();
                            const rv_hour = slot.date.getHours();
                            const rv_minute = slot.date.getMinutes();
                            const rv_weekday = slot.date.getDay();
                            const weekdays = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"]

                            const rv_label1 = `${weekdays[rv_weekday]}  ${rv_day} ${monthNamesFR[rv_month].toLowerCase()} ${rv_year}`;
                            const rv_label2 = `de ${rv_hour} h ${rv_minute.toFixed().padStart(2, '0')} à ${rv_hour + 1} h ${rv_minute.toFixed().padStart(2, '0')}`;

                            return {
                                label1: rv_label1,
                                label2: rv_label2,
                                value: slot.date.getTime(),
                                isDisabled: false,
                                current: false,
                            }
                        })
                        ;
                    console.log(rvslots);

                    if (rvslots.length === 0) {
                        toggleSimpleRendezvous(null);
                        return;
                    }
                    setRendezvousOptions(rvslots);
                }
            })
            .catch(setError)
            ;
    }, [selectedYear, selectedMonth])

    React.useEffect(() => {
        if (debug.indexOf("all") >= 0) console.log(`calendar: selected day set to ${selectedDay}`);

        setDaySlots(null);
        setSelectedHour(-1);
        if (selectedDay < 0)
            return;

        if (debug.indexOf("all") >= 0) console.log(`calendar: loading slots`);

        if ((selectedYear !== null) && (selectedMonth !== null)) {
            api_get_slots(uuid, token, selectedYear.value, selectedMonth.value, selectedDay)
                .then(slots => {
                    setDaySlots(slots.map(slot => {
                        if (typeof slot.date === "string") {
                            slot.date = new Date(slot.date);
                        }
                        return slot;
                    }));
                })
                .catch(setError)
                ;
        }
    }, [selectedDay])

    React.useEffect(() => {
        setPage(0);
        if (daySlots !== null) {
            setHours(compute_hours_from_slots(daySlots, resolution));
        } else {
            setHours([]);
        }
    }, [daySlots])

    // -----------------------------

    useEffect(() => {
        if (msgSource && msgOrigin) {
            console.log(`message: messaage reply: ${calTicket}`);
            msgSource.postMessage({
                event: "calendar.ready",
                ticket: calTicket,
            }, msgOrigin as any);
        }
    }, [msgSource])

    // ---

    useEffect(() => {
        console.log(`message: waiting for messages`);
        window.onmessage = (evt: MessageEvent) => {
            console.log(`message: got message ${JSON.stringify(evt.data)}`);
            if (typeof evt.data !== "object") {
                return;
            }
            if (evt.data.event === "load") {
                // setMsgOrigin(evt.origin);
                setMsgOrigin("*");
                setMsgSource(evt.source);
                setCalTicket(evt.data.ticket);
            }
        }
    }, []);

    // ---

    const refToTop = useRefScrollTo({ behavior: 'auto', block: 'start', inline: 'nearest' });

    // ---

    if (paramError !== "") {
        return <div style={{ ...defaultStyle, maxWidth: "700px", margin: "10px auto" }}>
            {paramError}
        </div>
    }

    if (loadingCrmInfo) {
        return <div style={{ ...defaultStyle, maxWidth: "700px", margin: "10px auto" }}>
            Chargement des données depuis la CRM
        </div>
    }

    if (registrationPending) {
        return <div style={{ ...defaultStyle, maxWidth: "700px", margin: "10px auto" }}>
            Réservation du rendezvous en cours, veuillez patienter...
        </div>
    }

    // ---

    const onChangeYear = (newOption: MultiValue<Option> | SingleValue<Option>, actionMeta: ActionMeta<Option>) => {
        const newOption2 = newOption as SingleValue<Option>;
        if (newOption2 != null) {
            setSelectedYear(newOption2);
            setSelectedMonth(null);
            setSelectedHour(-1);
            setSelectedDay(-1);
        }
    }

    const onClickRendezvous = (option: NewOption) => {
        setRendezvousOptions(options => {
            const new_options = [...options];
            new_options.forEach(new_option => {
                new_option.current = option.value == new_option.value;
            });
            return new_options;
        })
        console.log(`change rendez-vous: %o`, option);
        if (option) {
            SetSelectedRendezvous(option);
        }
    }

    const toggleSimpleRendezvous = (_evt: React.MouseEvent | null) => {
        setSimpleView(value => value > 0 ? 0 : def_simple_view);
    }

    const onChangeMonth = (newOption: MultiValue<Option> | SingleValue<Option>, actionMeta: ActionMeta<Option>) => {
        const newOption2 = newOption as SingleValue<Option>;
        if (newOption2 != null) {
            setSelectedMonth(newOption2);
            setSelectedHour(-1);
            setSelectedDay(-1);
        }
    }

    const onSelectDay = (num) => (evt: React.MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();
        setSelectedDay(num);
        if (debug.indexOf("all") >= 0) console.log(`calendar: onSelectDay ${num}`);
    }

    const onSelectHour = (hour: Hour) => (evt: React.MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();
        setSelectedHour(hour.value);
        if (debug.indexOf("all") >= 0) console.log(`calendar: onSelectHour %o`, hour);
    }

    const setPrevPage = (evt: React.MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();
        setPage(page - 1);
        setSelectedHour(-1);
    }

    const setNextPage = (evt: React.MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();
        setPage(page + 1);
        setSelectedHour(-1);
    }

    // ---

    const onClickInviteeCivility = (newCivility: string) => (evt: React.MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();
        setInviteeCivility(newCivility);
    }

    const onChangeInviteeFirstName = (evt: React.ChangeEvent<HTMLInputElement>) => {
        const value = evt.currentTarget.value;
        setInviteeFirstName(value);
    }

    const onChangeInviteeLastName = (evt: React.ChangeEvent<HTMLInputElement>) => {
        const value = evt.currentTarget.value;
        setInviteeLastName(value);
    }

    const onChangeInviteeEmail = (evt: React.ChangeEvent<HTMLInputElement>) => {
        const value = evt.currentTarget.value;
        setInviteeEmail(value);
    }

    const onChangeInviteePhone = (evt: React.ChangeEvent<HTMLInputElement>) => {
        const value = evt.currentTarget.value;
        setInviteePhone(value);
    }

    const onChangeCanRegister = (checked: boolean) => {
        setCanRegister(checked);
    }

    const onTryAgain = (evt: React.MouseEvent<HTMLButtonElement>) => {
        if (daySlots == null) {
            return;
        }

        setError("");
        setCanRetry(false);

        setHours(oldHours => {
            const tmp = [] as Hour[];
            oldHours.forEach((hour) => {
                if (hour.value !== selectedHour) {
                    tmp.push(hour);
                }
            })
            return tmp;
        });
        setSelectedHour(-1);
        setAlreadyTaken(false);
    }

    // --

    const reserveSlot = (evt: React.MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();

        if (calendar === null) {
            return
        }

        // This can be a 2 steps process : 
        //  1. "fill form data", then "register and show result"
        //  2. "register and show result"

        console.log(`reserveSlot: showReserve=${showReserve?"true":"false"} iframeFrom=${iframeFrom}`);

        if (!showReserve || (iframeFrom === "form")) {
            registerSlot(evt);
            return
        }

        // go to step "fill form data"
        setReservedSlot(true);
    }

    const cancelRequest = (evt: React.MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();

        console.log(`cancelRequest [1]`);

        if (calendar === null) {
            console.log(`cancelRequest: calendar === null`);
            return
        }

        if (msgSource === null) {
            console.log(`cancelRequest: msgSource === null`);
            return
        }

        if (msgOrigin === null) {
            console.log(`cancelRequest: msgOrigin === null`);
            return
        }

        console.log(`cancelRequest: msgSource %o`, msgSource);
        console.log(`cancelRequest: sending message to %o`, msgOrigin);
        msgSource.postMessage({
            event: "calendar.cancel",
        }, msgOrigin as any);
    }

    const registerSlot = (evt: React.MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();
        if (calendar === null) {
            return
        }

        console.log(`rvslot: scroll to top`);
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

        let date = new Date();
        if (selectedRendezvous !== null) {
            date = new Date(selectedRendezvous.value);
        } else if (selectedYear === null || selectedMonth === null || selectedHour === null) {
            return
        } else {
            date = new Date(
                selectedYear.value,
                selectedMonth.value - 1, // month
                selectedDay,
                Math.floor(selectedHour),
                Math.round((selectedHour * 60) % 60),
            );
        }

        const request = {
            year: date.getUTCFullYear(),
            month: date.getUTCMonth() + 1, // pfff
            day: date.getUTCDate(),
            hour: date.getUTCHours(),
            minute: date.getUTCMinutes(),
            persons: [
                {
                    civility: inviteeCivility,
                    first_name: inviteeFirstName,
                    last_name: inviteeLastName,
                    email: inviteeEmail,
                    phone: fixPhone(inviteePhone),
                },
            ],
            payload: {
                deferred_guid: deferredGuid,
                deferred_time: deferredTime,
                account_guid: accountGuid,
                contact_guid: contactGuid,
                alternate_guid: alternateGuid,
                account_to_call: accountToCall,
                no_confirm_email: noConfirmEmail ? "true" : "",
            }
        } as CalendarEventRequest;

        setRegistrationPending(true);
        api_register_event(uuid, token, request)
            .finally(() => {
                setRegistrationPending(false);
            })
            .then((data) => {
                console.log(`calendar: set registered: %o`, data);
                setRegistered(data.id);
            })
            .catch((api_error) => {
                if (api_error instanceof ApiError) {
                    const status = api_error.statusCode;

                    if (status === 409) {
                        setAlreadyTaken(true);
                        setRegistered(null);
                        setReservedSlot(false);
                        return;
                    }

                    if (status === 0) {
                        setError('erreur réseau, nouvelle tentative dans 10 secondes');
                        setTimeout(() => {
                            api_register_event(uuid, token, request)
                                .finally(() => {
                                    setError("");
                                })
                                .then((data) => {
                                    console.log(`calendar: set registered: %o`, data);
                                    setRegistered(data.id);
                                })
                                .catch((api_error) => {
                                    if (api_error instanceof ApiError) {
                                        const status = api_error.statusCode;

                                        if (status === 409) {
                                            setAlreadyTaken(true);
                                            setRegistered(null);
                                            setReservedSlot(false);
                                            return;
                                        }
                                    }

                                    setCanRetry(true);
                                    setError(`erreur2 ${status}: ${calendar.now} -> ${api_error.message} uuid=${uuid} ${JSON.stringify(request)}`);
                                    return;
                                });
                        }, 1000 * 10);
                        return;
                    }

                    setError(`erreur ${status}: ${calendar.now} -> ${api_error.message} uuid=${uuid} ${JSON.stringify(request)}`);
                    return;
                }

                setError(`unknown error: ` + JSON.stringify(api_error));
            })
            ;
    }

    // ---

    if (calendar == null) {
        console.log(`loading`);
        return <div>
            <div style={textStyle} className="opacity-75">Chargement du calendrier ...</div>
        </div>
    }

    if (error) {
        console.log(`error: ${error}`);
        return <div className="center-body">
            <div style={{ ...textStyle, color: `#${parameters.errColor}` }}>{error}</div>
            {canRetry && <div className="text-start">
                <MyButton
                    className="my-btn"
                    style={hourBtnEnabledStyle}
                    hoverStyle={hourBtnHoverStyle}
                    onClick={onTryAgain}>
                    Cliquez ici pour essayer à nouveau
                </MyButton>
            </div>}
        </div>
    }

    if (alreadyTaken) {
        console.log(`alreadyTaken`);
        return <>
            <div className="center-body">
                <div style={textStyle} className="text-start">
                    Désolé, mais ce créneau a été réservé par une autre personne, veuillez recommencer.
                </div>

                <div className="text-start">
                    <MyButton
                        className="my-btn"
                        style={hourBtnEnabledStyle}
                        hoverStyle={hourBtnHoverStyle}
                        onClick={onTryAgain}>
                        Cliquez ici pour prendre un nouveau créneau
                    </MyButton>
                </div>
            </div>
        </>
    }

    const selectContact = (evt: React.ChangeEvent<HTMLSelectElement>) => {
        const target = evt.target;
        const value = target.value;

        const c = crmContacts.find(c => c.uuid == value);
        if (c !== undefined) {
            setContactGuid(c.uuid);
            setInviteeCivility(c.civility);
            setInviteeFirstName(c.first_name);
            setInviteeLastName(c.last_name);
            setInviteeEmail(c.email);
            setInviteePhone(c.phone);
        }
    }

    // ---

    if (registered !== null) {
        console.log(`registered`);
        let sd: null | UtcDate = null;
        if (selectedRendezvous) {
            const date = new Date(selectedRendezvous.value);
            const year = date.getFullYear();
            const month = date.getMonth();
            const day = date.getDate();
            const hour = date.getHours();
            const minute = date.getMinutes();
            sd = {
                year,
                month,
                day,
                hour,
                minute,
            }
        }

        return <div id="thankyou" className={defDoSpace ? "" : "nospace"} ref={refToTop}>
            <h3 style={{
                ...textStyle,
                margin: "10px 0",
                fontSize: "1.4em",
            }}>{title}</h3>
            {/* <p style={{
                ...textStyle,
                margin: "10px 0",
            }}>Une invitation a été envoyée par SMS.</p> */}
            <div style={{
                ...textStyle,
                // border: "1px solid #000",
                // padding: "10px",
            }}>
                {(defCivility != "" && defFirstName != "" && defLastName != "") ? <p>
                    {defCivility} {defLastName}, nous avons le plaisir de vous confirmer votre rendez-vous avec le service après obsèques le 
                    <> {sd ? sd.day : selectedDay} </>
                    <> {sd ? monthNamesFR[sd.month] : selectedMonth?.label} </>
                    <> {sd ? sd.year : selectedYear?.label} </>
                    <> entre </>
                    {sd ? <>
                        {sd.hour} h {sd.minute || 0}
                    </> : <>
                        {humanHour(selectedHour)}
                    </>}
                    <> et </>
                    {sd ? <>
                        {sd.hour + 1} h {sd.minute || 0}
                    </> : <>
                        {humanHour(selectedHour + 1)}
                    </>}
                </p> : <p>
                    Date réservée : 
                    <> {sd ? sd.day : selectedDay} </>
                    <> {sd ? monthNamesFR[sd.month] : selectedMonth?.label} </>
                    <> {sd ? sd.year : selectedYear?.label} </>
                    <> à </>
                    {sd ? <>
                        {sd.hour} h {sd.minute || 0}
                    </> : <>
                        {humanHour(selectedHour)}
                    </>}
                </p>}
            </div>
        </div>
    }

    // ---

    if (reservedSlot) {
        const disabled = true && (
            (inputs.has("civility") && (inviteeCivility == "")) ||
            (inputs.has("name") && ((inviteeFirstName == "") || (inviteeLastName == ""))) ||
            (inputs.has("email") && (!isValidEmail(inviteeEmail))) ||
            (inputs.has("phone") && (!isValidPhone(inviteePhone))) ||
            false
        );

        return <>
            <header>
                <h3 style={textStyle}>{title}</h3>
                {!hide_resolution && <p style={textStyle}>
                    <svg viewBox="0 0 32 32">
                        <circle cx={16} cy={16} r={16}></circle>
                        <line x1={16} y1={16} x2={16} y2={4} stroke-width={2}></line>
                        <line x1={16} y1={16} x2={23} y2={10} stroke-width={2}></line>
                    </svg>
                    {resolution} minutes
                </p>}
            </header>

            <nav>
                <h3 style={textStyle}>
                    Date réservée : {selectedDay} {selectedMonth?.label} {selectedYear?.label}
                    <> à </>
                    {humanHour(selectedHour)}
                </h3>
            </nav>

            <form>
                {crmContacts.length > 1 && (
                    <section className="center">
                        <div>Sélectionnez le contact :</div>
                        <select onChange={selectContact}>
                            {crmContacts.map(c => <option value={c.uuid}>
                                {c.civility} {c.first_name} {c.last_name} {c.email}
                            </option>)}
                        </select>
                    </section>
                )}

                <h3 style={textStyle}>Information pour le rendez-vous</h3>
                {inputs.has("civility") && (
                    <>
                        <div>
                            <label style={textStyle}>Civilité</label>
                        </div>
                        <div>
                            <MyButton
                                onClick={onClickInviteeCivility("Mme")}
                                selected={inviteeCivility == "Mme"}
                                style={civilityBtnEnabledStyle}
                                hoverStyle={civilityBtnHoverStyle}
                                selectedStyle={civilityBtnSelectedStyle}
                                className="my-btn">
                                Madame
                            </MyButton>
                            <MyButton
                                onClick={onClickInviteeCivility("M")}
                                selected={inviteeCivility == "M"}
                                style={civilityBtnEnabledStyle}
                                hoverStyle={civilityBtnHoverStyle}
                                selectedStyle={civilityBtnSelectedStyle}
                                className="my-btn">
                                Monsieur
                            </MyButton>
                        </div>
                    </>
                )}
                {inputs.has("name") && (
                    <div className="two-columns">
                        <div>
                            <label style={textStyle} htmlFor="firstname-input" className="form-label">
                                Prénom <span style={textErrorStyle}>*</span>
                            </label>
                            <MyInput
                                id="firstname-input" type="text"
                                className="my-form-control"
                                onChange={onChangeInviteeFirstName}
                                value={inviteeFirstName}
                                enabledStyle={inputEnabledStyle}
                                disabledStyle={inputDisabledStyle}
                                hoverStyle={inputHoverStyle}
                                focusStyle={inputFocusStyle}
                                errorStyle={inputErrorStyle}
                                textErrorStyle={{ color: `#${parameters.errColor}` }}
                                placeholder="Prénom" />
                        </div>

                        <div>
                            <label style={textStyle} htmlFor="lastname-input" className="form-label">
                                Nom <span style={textErrorStyle}>*</span>
                            </label>
                            <MyInput
                                id="lastname-input" type="text"
                                className="my-form-control"
                                onChange={onChangeInviteeLastName}
                                value={inviteeLastName}
                                enabledStyle={inputEnabledStyle}
                                disabledStyle={inputDisabledStyle}
                                hoverStyle={inputHoverStyle}
                                focusStyle={inputFocusStyle}
                                errorStyle={inputErrorStyle}
                                textErrorStyle={{ color: `#${parameters.errColor}` }}
                                placeholder="Nom" />
                        </div>
                    </div>
                )}
                {inputs.has("email") && (
                    <div>
                        <label style={textStyle} htmlFor="email-input" className="form-label">
                            Email <span style={textErrorStyle}>*</span>
                        </label>
                        <MyInput
                            id="email-input" type="text"
                            className="my-form-control"
                            onChange={onChangeInviteeEmail}
                            value={inviteeEmail}
                            enabledStyle={inputEnabledStyle}
                            disabledStyle={inputDisabledStyle}
                            hoverStyle={inputHoverStyle}
                            focusStyle={inputFocusStyle}
                            errorStyle={inputErrorStyle}
                            textErrorStyle={{ color: `#${parameters.errColor}` }}
                            placeholder="Adresse Email" />
                    </div>
                )}
                {inputs.has("phone") && (
                    <div>
                        <label style={textStyle} htmlFor="phone-input" className="form-label">
                            Téléphone <span style={textErrorStyle}>*</span>
                        </label>
                        <MyInput
                            id="phone-input" type="phone"
                            className="my-form-control"
                            onChange={onChangeInviteePhone}
                            value={inviteePhone}
                            enabledStyle={inputEnabledStyle}
                            disabledStyle={inputDisabledStyle}
                            hoverStyle={inputHoverStyle}
                            focusStyle={inputFocusStyle}
                            errorStyle={inputErrorStyle}
                            textErrorStyle={{ color: `#${parameters.errColor}` }}
                            placeholder="Numéro de téléphone" />
                        {/* <div style={textStyle} className="fs-6 opacity-75">{fixPhone(inviteePhone)}</div> */}
                    </div>
                )}
                <div>
                    <MyButton
                        className="my-btn"
                        disabled={disabled}
                        style={formBtnEnabledStyle}
                        disabledStyle={formBtnDisabledStyle}
                        hoverStyle={formBtnHoverStyle}
                        onClick={registerSlot}>
                        Réserver ce créneau
                    </MyButton>
                </div>
            </form>
        </>
    }

    // ---

    const maxPages = Math.ceil(hours.length / itemsPerPage) - 1;

    const optionStyles: StylesConfig<Option> = {
        control: (styles, props) => {
            // console.log(`control: %o`, props);
            return { ...styles, ...defaultStyle, backgroundColor: parameters.input };
        },
        placeholder: (styles, props) => {
            // console.log(`placeholder: %o`, props);
            return { ...styles, ...defaultStyle, color: parameters.tinput }
        },
        singleValue: (styles, props) => {
            if (props.isDisabled) {
                return { ...styles, ...defaultStyle, color: parameters.dtinput }
            }
            //console.log(`singleValue: ${parameters.tinput}: %o`, props);
            return { ...styles, ...defaultStyle, color: parameters.tinput }
        },
        // valueContainer: (styles) => ({ ...styles, color: parameters.tinput }),
        // placeholder: (styles) => ({ ...styles, color: parameters.tinput }),
        // container: (styles) => ({ ...styles, backgroundColor: parameters.input }),
        option: (styles, { data, isDisabled, isFocused, isSelected }) => {
            return {
                ...styles,
                ...defaultStyle,
                color: isDisabled ? "#ccc" : "#000",
                fontWeight: data.current ? "bold" : "normal",
            }
        }
    }

    return (
        <>
            <header>
                <h2 style={textStyle}>{title}</h2>
                {!hide_resolution && <p style={textStyle}>
                    <svg viewBox="0 0 32 32">
                        <circle cx={16} cy={16} r={16}></circle>
                        <line x1={16} y1={16} x2={16} y2={4} stroke-width={2}></line>
                        <line x1={16} y1={16} x2={23} y2={10} stroke-width={2}></line>
                    </svg>
                    {resolution} minutes
                </p>}
            </header>

            {def_simple_view > 0 && <div style={{ ...defaultStyle, maxWidth: "700px", margin: "0 auto" }}>
                {(defCivility != "" && defFirstName != "" && defLastName != "") ? <p>
                    Bonjour {defCivility} {defLastName} et bienvenue au service après obsèques.
                </p> : <p>
                    Bonjour et bienvenue au service après obsèques.
                </p>}

                <p>Prenez rendez-vous avec l’un de nos assistants ou contactez-nous au <a style={{ "color": "#000" }} href="tel:0180202190">0180202190</a>.</p>

                <p>Vous allez être sensibilisé sur l’ensemble des démarches administratives à réaliser après un décès.</p>
            </div>}

            {simple_view <= 0 && <nav>
                <h2 style={textStyle}>
                    Selectionnez la date et l'heure :
                </h2>
                <div id="datepicker">
                    {yearOptions.length == 1 ? (
                        <div>
                            <Select
                                getOptionLabel={getLabel}
                                getOptionValue={getValue}
                                className="basic-single"
                                classNamePrefix="select"
                                value={selectedYear}
                                isDisabled={true}
                                name="year"
                                options={yearOptions}
                                onChange={onChangeYear}
                                styles={optionStyles}
                            />
                        </div>
                    ) : (
                        <div>
                            <Select
                                getOptionLabel={getLabel}
                                getOptionValue={getValue}
                                className="basic-single"
                                classNamePrefix="select"
                                value={selectedYear}
                                name="year"
                                options={yearOptions}
                                onChange={onChangeYear}
                                styles={optionStyles}
                            />
                        </div>
                    )}

                    <div>
                        <Select
                            getOptionLabel={getLabel}
                            getOptionValue={getValue}
                            className="basic-single"
                            classNamePrefix="select"
                            value={selectedMonth}
                            name="month"
                            options={monthOptions}
                            onChange={onChangeMonth}
                            styles={optionStyles}
                        />
                    </div>
                </div>
            </nav>}

            {loadingSlots ? <>
                <div style={{ ...defaultStyle, maxWidth: "700px", margin: "10px auto" }}>Chargement des données ...</div>
            </> : simple_view > 0 ? <>
                {rendezvousOptions.length > 0 && <div id="calendar-simple">
                    <div className="calendar-buttons">
                        {rendezvousOptions.map(option => <>
                            <button
                                className={option.current ? "selected" : ""}
                                type="button" onClick={() => onClickRendezvous(option)}>
                                <div>{option.label1}</div>
                                <div>&nbsp;{option.label2}&nbsp;</div>
                                <span onClick={reserveSlot} className="confirm">Confirmer</span>
                            </button>
                            <div style={{
                                ...defaultStyle,
                                color: "#ccc",
                            }}>En confirmant le rendez-vous, j'accepte que mes données soient utilisées pour traiter la demande.</div>
                        </>)}
                    </div>

                    <div style={defaultStyle}>
                        Si aucun de ces rendez-vous ne vous convient, cliquez sur le bouton "Autres disponibilités" pour choisir un autre rendez-vous.
                    </div>

                    <div style={defaultStyle}>
                        <MyButton
                            className="my-btn"
                            disabled={false}
                            style={hourBtnEnabledStyle}
                            hoverStyle={hourBtnHoverStyle}
                            disabledStyle={hourBtnDisabledStyle}
                            onClick={toggleSimpleRendezvous}>
                            {"Autres disponibilités"}
                        </MyButton>
                    </div>
                </div>}
            </> : <>
                <div id="calendar">
                    <div id="daypicker">
                        <div id="dayheader">
                            {weekDaysFR.map(weekDay => (
                                <div style={smallTextStyle} className="day">
                                    {weekDay}
                                </div>
                            ))}
                        </div>

                        {firstDayOfMonth == 0 && (
                            <div className="line">
                                {weekDaysFR.map((_, i) => {
                                    const thisDayIs1 = 1 == thisDay;
                                    const thisMonthMatch = thisMonth == selectedMonth?.value;
                                    const thisYearMatch = thisYear == selectedYear?.value;
                                    const isToday = thisDayIs1 && (thisMonthMatch) && (thisYearMatch);
                                    const beforeTomorrow = (showTodaysSlots == false) ?
                                        1 <= thisDay && thisMonthMatch && thisYearMatch :
                                        1 < thisDay && thisMonthMatch && thisYearMatch;
                                    const afterMaxDays = def_max_days <= 0 ? false : 1 > thisDay + def_max_days;
                                    const disabled = beforeTomorrow || afterMaxDays;

                                    const selected = (selectedDay == 1);
                                    return i < 6 ? (
                                        <div className="noday"></div>
                                    ) : (
                                        <div className="day">
                                            <MyButton
                                                onClick={onSelectDay(1)}
                                                disabled={disabled || loadingSlots}
                                                marked={isToday}
                                                selected={selected}
                                                style={calendarBtnEnabledStyle}
                                                disabledStyle={calendarBtnDisabledStyle}
                                                hoverStyle={calendarBtnHoverStyle}
                                                markedStyle={calendarBtnMarkedStyle}
                                                selectedStyle={calendarBtnSelectedStyle}
                                            >
                                                {1}
                                            </MyButton>
                                        </div>
                                    )
                                })}
                            </div>
                        )}

                        {/* all days ? */}

                        {iter_map(iter(1, lastDayOfMonth + 7, 7), (num) => (
                            <div className="line">
                                {weekDaysFR.map((_, i) => {
                                    const item = num + i;
                                    const day = item - (firstDayOfMonth - 1);

                                    // console.log(`foo: i=${i} item=${item} day=${day} thisDay=${thisDay}`);

                                    if ((day < 1) || (day > lastDayOfMonth)) {
                                        return <div className="noday"></div>;
                                    }

                                    const thisMonthMatch = thisMonth == selectedMonth?.value;
                                    const thisYearMatch = thisYear == selectedYear?.value;
                                    const beforeTomorrow = // day <= thisDay && thisMonthMatch && thisYearMatch;
                                        (showTodaysSlots == false) ?
                                        day <= thisDay && thisMonthMatch && thisYearMatch :
                                        day < thisDay && thisMonthMatch && thisYearMatch;
                                    const afterMaxDays = def_max_days <= 0 ? false : day > thisDay + def_max_days;
                                    const forceDisabled = beforeTomorrow || afterMaxDays;

                                    let disabled = true;
                                    monthSlots.forEach((slot: CalendarSlot) => {
                                        const slotDay = slot.date.getDate();
                                        if (day == slotDay) {
                                            if (slot.count > 0) {
                                                disabled = false;
                                            }
                                        }
                                    });
                                    disabled = disabled || forceDisabled;

                                    const isToday = ((day == thisDay) && (thisYear == selectedYear?.value) && (thisMonth == selectedMonth?.value));
                                    const selected = (selectedDay == day);

                                    return <div className="day">
                                        <MyButton
                                            onClick={onSelectDay(day)}
                                            disabled={disabled || loadingSlots}
                                            marked={isToday}
                                            selected={selected}
                                            style={calendarBtnEnabledStyle}
                                            disabledStyle={calendarBtnDisabledStyle}
                                            hoverStyle={calendarBtnHoverStyle}
                                            markedStyle={calendarBtnMarkedStyle}
                                            selectedStyle={calendarBtnSelectedStyle}
                                        >
                                            {day}
                                        </MyButton>
                                    </div>
                                })}
                            </div>
                        ))}
                    </div>

                    {/* hours */}

                    {(selectedDay != -1) && (
                        <div id="hourpicker">
                            {daySlots == null ? (
                                <div style={textStyle}>
                                    Chargement ...
                                </div>
                            ) : daySlots.length == 0 ? (
                                <div>
                                    Pas de rendez-vous disponible
                                </div>
                            ) : (
                                <>
                                    <div id="hourlist">
                                        {
                                            hours
                                                // limit slots per page
                                                .filter((_: Hour, idx: number) => {
                                                    return (idx >= page * itemsPerPage) && (idx < (page + 1) * itemsPerPage);
                                                })
                                                // generate slot buttons
                                                .map((hour: Hour) => {
                                                    const disabled = !hour.enabled;
                                                    const selected = selectedHour == hour.value;

                                                    return <div className="hour">
                                                        <MyButton
                                                            onClick={onSelectHour(hour)}
                                                            selected={selected}
                                                            disabled={disabled}
                                                            style={hourBtnEnabledStyle}
                                                            disabledStyle={hourBtnDisabledStyle}
                                                            hoverStyle={hourBtnHoverStyle}
                                                            selectedStyle={hourBtnSelectedStyle}
                                                        >
                                                            {hour.label}
                                                        </MyButton>
                                                    </div>
                                                })
                                        }
                                    </div>
                                    <div id="hourfooter">
                                        {page != 0 ? (
                                            <MyButton
                                                className="my-btn"
                                                style={formBtnEnabledStyle}
                                                hoverStyle={formBtnHoverStyle}
                                                onClick={setPrevPage}>
                                                &larr;
                                            </MyButton>
                                        ) : (
                                            <div></div>
                                        )}
                                        {(page < maxPages) ? (
                                            <MyButton
                                                className="my-btn"
                                                style={formBtnEnabledStyle}
                                                hoverStyle={formBtnHoverStyle}
                                                onClick={setNextPage}>
                                                &rarr;
                                            </MyButton>
                                        ) : (
                                            <div></div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </>}

            {(selectedDay >= 0 && selectedHour >= 0) && (
                <div id="footer" className={one_line_submit ? "oneline" : ""}>
                    <div>
                        <MyCheckbox
                            id="flexCheckDefault"
                            className="form-check-input"
                            enabledStyle={checkBoxEnabledStyle}
                            focusStyle={checkBoxFocusStyle}
                            onChange={onChangeCanRegister}
                        />
                        <label style={textStyle} className="form-check-label" htmlFor="flexCheckDefault">
                            J'accepte que mes données soient utilisées pour traiter la demande.
                        </label>
                    </div>
                    <div className="right">
                        <MyButton
                            className="my-btn"
                            disabled={!canRegister}
                            style={formBtnEnabledStyle}
                            hoverStyle={formBtnHoverStyle}
                            disabledStyle={formBtnDisabledStyle}
                            onClick={reserveSlot}>
                            {showReserve ? "Suivant" : "Réserver ce créneau"}
                        </MyButton>
                    </div>
                </div>
            )}

            {!loadingSlots && (def_company == "test") && <div id="cancel-div">
                <div style={textStyle}>Je ne suis pas intéressé{defCivility == "M" ? "" : "e"} par le service et je souhaite ne plus être contacté{defCivility == "M" ? "" : "e"}.</div>
                <div>
                    <MyButton
                        className="my-btn"
                        disabled={false}
                        style={formBtnEnabledStyle}
                        hoverStyle={formBtnHoverStyle}
                        disabledStyle={formBtnDisabledStyle}
                        onClick={cancelRequest}>
                        {"Cliquez ici pour ne plus être contacté" + ((defCivility == "M") ? "" : "e")}
                    </MyButton>
                </div>
            </div>}

            {debug.indexOf("all") >= 0 && (
                <div id="debug">
                    <div>
                        resolution: {resolution}, page: {page}
                    </div>
                    <div>
                        thisYear: {thisYear}, thisMonth: {thisMonth}, thisDay: {thisDay}
                    </div>
                    <div>
                        selectedYear: {selectedYear?.value}, selectedMonth: {selectedMonth?.value}
                    </div>
                    <div>
                        selectedHour: {selectedHour}, selectedDay: {selectedDay}
                    </div>
                    <div>
                        firstDayOfMonth: {firstDayOfMonth}, lastDayOfMonth: {lastDayOfMonth}
                    </div>
                    <h3>calendar-metas:</h3>
                    <div>
                        {Object.keys(calendar.calendar_metas).map(key => <div>
                            metas.{key}: {calendar.calendar_metas[key]}
                        </div>)}
                    </div>
                    <h3>instance-metas:</h3>
                    <div>
                        {Object.keys(calendar.instance_metas).map(key => <div>
                            metas.{key}: {calendar.instance_metas[key]}
                        </div>)}
                    </div>
                </div>
            )}
        </>
    )
}

export default Form;
